"use client";

import { useEffect, useId, useState } from "react";
import { Input } from "@tillix/ui-atoms/input";
import { roundCents } from "@tillix/utils/allocate-invoice-payments";
import { useT } from "@/features/i18n/hooks/use-t";

// TODO: call SAP average bill API, double it, and set that as the overpay limit
const MAX_OVERPAY = 50;

interface PaymentAmountInputProps {
  /** Current committed amount (source of truth from the parent). */
  amount: number;
  /** Account balance used to compute the overpay ceiling. */
  balance: number;
  onAmountChange: (amount: number | undefined) => void;
  className?: string;
}

export function PaymentAmountInput({
  amount,
  balance,
  onAmountChange,
  className,
}: PaymentAmountInputProps) {
  const inputId = useId();
  const { t } = useT();
  const maxAmount = balance + MAX_OVERPAY;

  // Local string state so the user can freely type; syncs to parent on blur.
  const [rawAmount, setRawAmount] = useState(amount.toFixed(2));

  // Only reformat when the amount changes externally (e.g. toggling full
  // balance). Skip if the local value already represents the same number
  // to avoid clobbering the user's cursor mid-typing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: rawAmount is intentionally read without reacting to it
  useEffect(() => {
    const local = Number.parseFloat(rawAmount);
    if (!Number.isFinite(local) || Math.abs(local - amount) > 0.004) {
      setRawAmount(amount.toFixed(2));
    }
  }, [amount]);

  const handleChange = (value: string) => {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > maxAmount) {
      const clamped = roundCents(maxAmount);
      setRawAmount(clamped.toFixed(2));
      onAmountChange(clamped);
    } else {
      setRawAmount(value);
      if (Number.isFinite(parsed) && parsed >= 0.01) {
        onAmountChange(roundCents(parsed));
      }
    }
  };

  const commitAmount = (value: string) => {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed >= 0.01 && parsed <= maxAmount) {
      onAmountChange(roundCents(parsed));
    } else {
      setRawAmount(amount.toFixed(2));
    }
  };

  return (
    <div className={className ?? "flex items-center gap-3"}>
      <label className="text-sm" htmlFor={inputId}>
        {t("paymentForm.amount")}
      </label>
      <Input
        className="min-w-0 flex-1"
        id={inputId}
        inputMode="decimal"
        max={maxAmount}
        min={0.01}
        onBlur={(e) => commitAmount(e.target.value)}
        onChange={(e) => handleChange(e.target.value)}
        step={0.01}
        type="number"
        value={rawAmount}
      />
    </div>
  );
}
